import { Deserializer } from 'jsonapi-serializer';
import { EnrolmentAudit, EnrolmentAuditWrapper } from '../models/EnrolmentAudit';
import { genericError } from '../models/GenericError';
import { ListQuery } from '../models/ListQuery';
import { getDateTimeWithoutMillis } from '../utilities/DateUtils';
import { getActionName, getEnrolmentType, getEnrolmentField, getNotificationField, getEnrolmentValue } from '../utilities/AuditUtils';
import { APIConnector } from './api/APIConnector';
import { createPagination } from "./utilities/paginationBuilder";
import { processServerError, getConstraintViolation } from './utilities/ServiceError';
import { createPartialURL, getFilterClause } from './utilities/urlBuilder';
import { Translator } from "../contexts/translator";
import { StoreEntityResult } from '../models/services/StoreEntityResult';
import { Name } from '@/models/Name';
import { PaginationModel } from '@/models/Pagination';

const apiConnector = new APIConnector();

/*
Audit records for a specific person.
E.g. edits on Master / MasterGroup / MasterFloor / MasterAsset / Tag / MasterExpiry / MasterDetail / etc.
*/
export const getAuditForPerson = async (masterId: string, listParameters?: ListQuery, translate?: Translator): Promise<EnrolmentAuditWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const url = `auditPerson?${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, records: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError })
                }
                
                const auditRecords: EnrolmentAudit[] = records.map((personAudit) => buildPersonAudit(personAudit, translate));
                return resolve({ rows: auditRecords, pagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

/*
  Build the audit record for a specific person.
*/
const buildPersonAudit: (value: any, translate) => EnrolmentAudit = (apiPersonAudit, translate?: Translator): EnrolmentAudit => {
    return {
        id: apiPersonAudit['id'],
        name: apiPersonAudit['type'],
        dateTime: getDateTimeWithoutMillis(apiPersonAudit['date-time-local']),
        operator: apiPersonAudit['operator'],
        action: getActionName(apiPersonAudit['type'], apiPersonAudit['action'], translate),
        type: getEnrolmentType(apiPersonAudit['type'], apiPersonAudit['action'], translate),
        field: getEnrolmentField(apiPersonAudit['field'], translate),
        valueOld: getEnrolmentValue(apiPersonAudit['value-old'], apiPersonAudit['field'], translate),
        valueNew: getEnrolmentValue(apiPersonAudit['value-new'], apiPersonAudit['field'], translate)
    };
};

/*
Audit records for a specific notification.
Does not audit the editing of the notification yet, just the failure to send it, auto-disabling, etc.
*/
export const getAuditForNotification = async (masterId: string, listParameters?: ListQuery, translate?: Translator): Promise<EnrolmentAuditWrapper> => {
    try {
        //const auditTypes = '(auditSubType.astName =in= (NOTIFICATIONS), auditType.atName =in=(VRule))'; // Either create/edit (VRule) or notification-related (NOTIFICATIONS)
        const auditTypes = '(auditSubType.astName =in= (NOTIFICATIONS))'; // notification-related (NOTIFICATIONS)
        let filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm, 'auditSession.dateOnly');
        filter = (filter === '' ? `&filter=${auditTypes}` : `${filter};${auditTypes}`);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const fieldsDetail = "fields[auditDetail]=auditSubType,auditSession,auditActionType,adValueNew";
        const fieldsSession = "fields[auditSession]=asDatetimelocal,asUserLoginName,dateOnly";
        const fieldsActionType = "fields[auditActionType]=trnName";
        const fieldsSubType = "fields[auditSubType]=trnName,astName";
        const includes = "include=auditSubType,auditSession,auditActionType";
        const sortBy = "sort=-auditSession.asDatetimelocal";
        const url = `auditDetail?${partialURL}&${includes}&${fieldsDetail}&${fieldsSession}&${fieldsActionType}&${fieldsSubType}&${sortBy}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, records: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError })
                }

                const auditRecords: EnrolmentAudit[] = records.map((personAudit) => buildNotificationAudit(personAudit, translate));
                return resolve({ rows: auditRecords, pagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

/*
  Build the audit record for a specific notification.
*/
const buildNotificationAudit: (value: any, translate) => EnrolmentAudit = (apiAudit, translate?: Translator): EnrolmentAudit => {
    return {
        id: apiAudit['id'],
        name: apiAudit['type'],
        dateTime: getDateTimeWithoutMillis(apiAudit['audit-session']['as-datetimelocal']),
        operator: apiAudit['audit-session']['as-user-login-name'],
        action: apiAudit['audit-action-type']['trn-name'],
        type: '',
        field: getNotificationField(apiAudit['audit-sub-type']['ast-name'], translate),
        valueOld: '',
        valueNew: apiAudit['ad-value-new'],
        // valueNew: getEnrolmentValue(apiPersonAudit['value-new'], apiPersonAudit['field'], translate)
    };
};
export const auditRunReport = async (reportId: string | number): Promise<StoreEntityResult> => {
    try {
        const newRecord = {
            type: "auditRunReport",
            attributes: {
                id: reportId
            }
        }

        let response;

        await apiConnector.sendData('', true, newRecord, true).then(data => {
            response = data;
        }).catch(error => {
            return Promise.reject(processServerError(error))
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (response.ok === false) {
                    const content = await response.json();
                    return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail) ?? genericError })
                }

                return resolve({ id: 0, success: true });
            }

            return reject({ status: response.status, message: genericError });
        })
    } catch (error) {
        return Promise.reject(processServerError(error))
    }
}

export const getAuditTypeNamesWithPagination = async (listQuery?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filters);
        const auditTypeFields = 'fields[auditType]=nameOriginal';
        const url = `auditType?${paginationString}&${auditTypeFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, apiAuditTypes: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const auditTypes: Name[] = apiAuditTypes.map((auditType): Name => {
                    return {
                        value: auditType['id'],
                        label: auditType['name-original'],
                        isDefault: true
                    };
                });

                return resolve({ rows: auditTypes, pagination: newPagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getAuditSubTypeNamesWithPagination = async (listQuery?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filters);
        const auditSubTypeFields = 'fields[auditSubType]=nameOriginal';
        const url = `auditSubType?${paginationString}&${auditSubTypeFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, apiAuditSubTypes: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const auditSubTypes: Name[] = apiAuditSubTypes.map((auditSubType): Name => {
                    return {
                        value: auditSubType['id'],
                        label: auditSubType['name-original'],
                        isDefault: true
                    };
                });

                return resolve({ rows: auditSubTypes, pagination: newPagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getAuditActionTypeNamesWithPagination = async (listQuery?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filters);
        const auditActionTypeFields = 'fields[auditActionType]=nameOriginal';
        const url = `auditActionType?${paginationString}&${auditActionTypeFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, apiAuditActionTypes: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const auditActionTypes: Name[] = apiAuditActionTypes.map((auditActionType): Name => {
                    return {
                        value: auditActionType['id'],
                        label: auditActionType['name-original'],
                        isDefault: true
                    };
                });

                return resolve({ rows: auditActionTypes, pagination: newPagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}